import { Component, computed, EventEmitter, inject, input, OnInit, Output, Renderer2, Signal } from '@angular/core';
import "@customer-web-app/JavaScript/ApplePayPayment.js";
import "@customer-web-app/JavaScript/CyberSourceCardPayment.js";
import "@customer-web-app/JavaScript/GooglePayPayment.js";
import "@customer-web-app/JavaScript/IFrameMessaging.js";
import "@customer-web-app/JavaScript/PayPalPayment.js";
import { BaseComponent } from '@shared-components/base-component';
import { ComponentHeaderComponent } from '@shared-components/component-header/component-header.component';
import { ManageCardComponent } from '@shared-components/manage-card/manage-card.component';
import { ModalComponent } from '@shared-components/modal/modal.component';
import { ProductDisplayComponent } from '@shared-components/product-display/product-display.component';
import { AppHelper } from '@shared-helpers/appHelper';
import { ProductHelper } from '@shared-helpers/productHelper';
import { UrlHelper } from '@shared-helpers/urlHelper';
import { ExpertModel } from '@shared-model/expert/ExpertModel';
import { ProductModel } from '@shared-model/products/ProductModel';
import { InitialisePaymentResult } from '@shared-results/payment/InitialisePaymentResult';
import { TransactionRequestResult } from '@shared-results/payment/TransactionRequestResult';
import { PaymentService } from '@shared-services/payment.service';

@Component({
  selector: 'fourd-payment',
  imports: [ModalComponent, ComponentHeaderComponent, ManageCardComponent, ProductDisplayComponent],
  templateUrl: './payment.component.html',
  host: {
    '[class]': 'chatClass'
  },
  providers: [...BaseComponent.componentProviders],
})
export class PaymentComponent extends BaseComponent implements OnInit {
  private paymentService = inject(PaymentService);
  private renderer = inject(Renderer2);

  readonly expert = input<ExpertModel>(undefined);
  readonly product = input<ProductModel>(undefined);
  readonly products = input<ProductModel[]>(undefined);
  productList: ProductModel[];
  selectedProduct: ProductModel;
  readonly modalDialog = input<boolean>(true);
  chatClass: string = "component-container";

  @Output() close = new EventEmitter<boolean>();

  hasRegisteredCard: boolean;
  currentCardNumberPartial: string;
  
  paymentLoading: boolean = true;
  submittingData: boolean = false;

  cyberSourceCardPaymentHelper: CyberSourceCardPaymentHelper = null;
  applePayPaymentHelper: ApplePayPaymentHelper = null;
  googlePayPaymentHelper: GooglePayPaymentHelper = null;
  payPalPaymentHelper: PayPalPaymentHelper = null;
  //iFrameMessagingHelper: IFrameMessagingHelper = null;
  paymentExecuting: boolean = false;
  showManageCard: boolean = false;

  useBalance: boolean = false;
  remainingAmount: number = 0;
  remainingAmountFormatted: string;

  manageCardsFunctionalityEnabled: Signal<ProductModel> = computed(() => this.moduleConfig?.ManageCardsFunctionalitySupported, { equal: AppHelper.objectsEqual });

  constructor() { super("PaymentComponent"); }

  ngOnInit() {
    // Set product list from products input if provided
    this.productList = this.products();
    if (!this.productList) {
      // Product list not set yet, set from proudct input
      this.productList = [this.product()];
    }
    // Set selected product from product input if provided
    this.selectedProduct = this.product();
    if (!this.selectedProduct && this.productList?.length == 1) {
      // Only one item in list, select it
      this.selectedProduct = this.productList[0];
    }

    this.ensureExternalScriptLoaded(
      UrlHelper.externalScriptLoggedInJs,
      (scriptPath: string, event: Event) => {
        // External JS loaded OK, continue with initialisation
        this.cyberSourceCardPaymentHelper = (typeof (CyberSourceCardPaymentHelper) !== 'undefined') ? new CyberSourceCardPaymentHelper() : null;
        this.applePayPaymentHelper = (typeof (ApplePayPaymentHelper) !== 'undefined') ? new ApplePayPaymentHelper() : null;
        this.googlePayPaymentHelper = (typeof (GooglePayPaymentHelper) !== 'undefined') ? new GooglePayPaymentHelper() : null;
        this.payPalPaymentHelper = (typeof (PayPalPaymentHelper) !== 'undefined') ? new PayPalPaymentHelper() : null;
        this.getPaymentData();
      }, // Success callback
      (scriptPath: string, event: Event) => {
        // Error loading external JS, stop initialisation
        this.constructModalError(
          "Sorry, there was an error initialising payment. Please try again.",
          () => { this.close.emit(false); }
        );
      } // Error callback
    );
    
  }

  requestPaymentV2(event: Event): void {
    this.handleClickEvent(event);
    this.cyberSourceCardPaymentHelper.onCreditCardPaymentButtonClicked();
  }

  getPaymentData(): void {
    this.paymentLoading = true;

    this.paymentService?.initialisePayment((result) => {
      if (result) {
        this.paymentLoading = false;

        this.hasRegisteredCard = result.HasRegisteredCard;
        this.currentCardNumberPartial = result.CurrentCardNumberPartial;
        if (this.currentCardNumberPartial?.length > 4) {
          this.currentCardNumberPartial = this.currentCardNumberPartial.substring(this.currentCardNumberPartial.length - 4);
        }

        this.initialiseCyberSourceCardPaymentHelper();

        if (AppHelper.isNullOrUndefined(result.ApplePayEnabled) ? this.config()?.ApplePayEnabled : result.ApplePayEnabled) {
          this.initialiseApplePay(result);
        }

        if (AppHelper.isNullOrUndefined(result.GooglePayEnabled) ? this.config()?.GooglePayEnabled : result.GooglePayEnabled) {
          this.ensureExternalScriptLoaded(
            UrlHelper.externalScriptGooglePay,
            (scriptPath: string, event: Event) => {
              // External JS loaded OK, continue with initialisation
              this.initialiseGooglePay(result);
            }, // Success callback
            (scriptPath: string, event: Event) => {
              // Error loading external JS, stop initialisation
              this.constructModalError(
                "Sorry, there was an error initialising payment. Please try again.",
                () => { this.close.emit(false); }
              );
            } // Error callback
            , false
          );
        }

        if (AppHelper.isNullOrUndefined(result.PayPalEnabled) ? this.config()?.PayPalEnabled : result.PayPalEnabled) {
          this.ensureExternalScriptLoaded(
            UrlHelper.externalScriptPayPal,
            (scriptPath: string, event: Event) => {
              // External JS loaded OK, continue with initialisation
              this.initialisePayPal(result);
            }, // Success callback
            (scriptPath: string, event: Event) => {
              // Error loading external JS, stop initialisation
              this.constructModalError(
                "Sorry, there was an error initialising payment. Please try again.",
                () => { this.close.emit(false); }
              );
            } // Error callback
            , false
          );
        }

      }
      else {
        this.constructModalError(
          "Sorry, an error occured initialising the payment. Please try again.",
          () => { this.close.emit(false); }
        );
      }
    });
    
    
  }

  initialiseCyberSourceCardPaymentHelper(): void {
    if (!this.cyberSourceCardPaymentHelper) {
      this.errorService?.logError("InitialiseCyberSourceCardPayment failed because CyberSourceCardPayment not defined", "initialiseCyberSourceCardPaymentHelper","initialiseCyberSourceCardPaymentHelper", false);

      this.constructModalError(
        "Sorry, an error occured initialising the payment. Please try again.",
        () => { this.close.emit(false); }
      );
    }

    if (!this.cyberSourceCardPaymentHelper.enabled) {
      let hasRegisteredCard: boolean = this.hasRegisteredCard;
      let iFramePaymentResultPage: string = UrlHelper.iFramePaymentResult;
      let registerCardUrl: string = this.moduleConfig?.UrlRegisterCardAndPay;
      let commonCallbacksOptions = {
        paymentTypeDescription: "Card"
      };
      let componentInstance: PaymentComponent = this;

      this.cyberSourceCardPaymentHelper.initialise({
        config: {
          hasRegisteredCard: hasRegisteredCard,
          returnUrl: (location.protocol + "//" + location.host + iFramePaymentResultPage),
          isEmbedded: true,
        },
        callbacks: Object.assign({},
          this.createCommonCallbacks(commonCallbacksOptions),
          {
            onPaymentButtonClickedNoRegisteredCard: function (productData: any) {
              if (componentInstance?.manageCardsFunctionalityEnabled()) {
                componentInstance.showManageCard = true;
              }
              else {
                if (productData && productData.productId) window.location.href = registerCardUrl + productData.productId;
                else window.location.href = registerCardUrl;
              }
            },
            //onRequestFieldsFetched: function (responseObject: any, productData: any) {
            //  IFrameMessaging.ResetFrame({});
            //  if (!IFrameMessaging.RegisterChild({
            //    childIdentifier: "fourd_payment_frame",
            //    contentUrl: UrlHelper.iFramePaymentForm,
            //    onMessage: function (messageData: any): any {
            //      if (messageData.action == IFrameMessagingAction.CyberSourcePayment && messageData.type == IFrameMessagingType.Ack) { //payment events
            //        if (!(messageData.payload && messageData.payload.taskAccepted)) {
            //          //TODO: could fallback to local form
            //          componentInstance.cyberSourceCardPaymentHelper.onError("CyberSourcePayment task could not be delegated", "IFrameMessaging/onMessage", true);
            //        }
            //      }

            //      if (messageData.action == IFrameMessagingAction.FrameInteractionDone) { //dismiss/continue selected on iFrame
            //        componentInstance.cyberSourceCardPaymentHelper.completePayment(messageData.payload);
            //      }

            //      return null;
            //    },
            //    onError: (err: any, stageText: any) => {
            //      componentInstance.errorService?.logError(`'${err}', error: '${stageText}'`, "Angular PaymentComponent IFrameMessaging", err, true);
            //    },
            //    onCompletion: function (isFrameLinkOk: any) {
            //      if (isFrameLinkOk) {
            //        IFrameMessaging.QueueMessage({
            //          type: IFrameMessagingType.Request,
            //          action: IFrameMessagingAction.CyberSourcePayment,
            //          payload: {
            //            productId: productData.productId,
            //            offerId: productData.offerId,
            //            amountToPay: productData.amountToPay,
            //            requestFieldsResponse: responseObject,
            //            isEmbedded: true
            //          }
            //        });
            //      }
            //      else {
            //        //TODO: could fallback to local form
            //        componentInstance.cyberSourceCardPaymentHelper.onError("IFrame link could not be established", "IFrameMessaging/onCompletion", true);
            //      }
            //    },
            //    completionTimeoutMillis: 30000
            //  })) {
            //    //TODO: could fallback to local form
            //    componentInstance.cyberSourceCardPaymentHelper.onError("IFrame setup failed", "IFrameMessaging/RegisterChild", true);
            //  }
            //}
          })
      });
    }
  }

  initialiseApplePay(initialisationData: InitialisePaymentResult): void {
    if (!this.applePayPaymentHelper) {
      this.errorService?.logError("InitialiseApplePay failed because ApplePayPayment not defined", "initialiseApplePay", "initialiseApplePay", false);

      this.constructModalError(
        "Sorry, an error occured initialising the payment. Please try again.",
        () => { this.close.emit(false); }
      );
    }

    if (!this.applePayPaymentHelper.enabled) {
      const applePayButtonElement = document.querySelector("#applepay-button");
      let commonCallbacksOptions = {
        paymentTypeDescription: "Apple Pay"
      };
      let componentInstance: PaymentComponent = this;

      this.applePayPaymentHelper.initialise({
        config: {
          merchantDisplayName: initialisationData?.ApplePayMerchantName,
          appleMerchantIdentifier: initialisationData?.ApplePayMerchantIdentifier,
          countryCode: initialisationData?.IsoCountryCodeTwoLetter,
          currencyCode: initialisationData?.Currency
        },
        callbacks: Object.assign({}, componentInstance.createCommonCallbacks(commonCallbacksOptions), {
          onIsReadyToPayResult: function (isAccepted: any) {
            if (isAccepted) {
              if (applePayButtonElement) {
                componentInstance.renderer?.removeClass(applePayButtonElement, "hidden");
                componentInstance.renderer?.listen(applePayButtonElement, 'click', (event: any) => {
                  componentInstance.applePayPaymentHelper.onApplePayButtonClicked();
                });
              }
            }
          }
        })
      });
    }
  }

  createCommonCallbacks(options: any) {

  if (!options) options = {};

  // Return callbacks array
  return {
    onProductDataRequested: () => { return this.getSelectedProductData(); },
    onError: (err: any, stageText: any, wasPaymentExecuting: any, wasProductMissing: any) => {

      this.errorService?.logError(`'${err}', error: '${stageText}'`, `Angular PaymentComponent, wasPaymentExecuting: ${wasPaymentExecuting}, wasProductMissing: ${wasProductMissing}`, err, true);

      if (typeof err === 'string' && err.includes('No response payload received')) {
        this.submittingData = false;
        this.paymentExecuting = false;
        return;
      }

      if (wasPaymentExecuting) {
        this.constructModalError(
          "Sorry, an unexpected error occured. Please try again.",
          () => { this.close.emit(false); }
        );

        this.submittingData = false;
        this.paymentExecuting = false;
      }
    },
    onPaymentExecuting: () => {
     //console.log("onPaymentExecuting");
      this.submittingData = true;
      this.paymentExecuting = true;
    },
    onPaymentCancelled: () => {
     // console.log("onPaymentCancelled");
      this.submittingData = false;
      this.paymentExecuting = false;
    },
    onPaymentExecuted: (isAccepted: boolean, responseData: TransactionRequestResult) => {
      //console.log("onPaymentExecuted:");// + JSON.stringify(responseData));
      this.submittingData = false;
      this.paymentExecuting = false;

      if (isAccepted) {
        // TODO - update TransactionRequestResult to include latest member data so that refresh not needed here
        this.subscriptionTracker?.add(this.memberService.refreshMember());
        this.constructModalInfo(
          `Successful ${options?.paymentTypeDescription ? options?.paymentTypeDescription + " " : ""}payment of ${responseData?.TransactionAmount?.AmountFormatted}`,
          () => { this.close.emit(true); }
        );
      }
      else {
        this.constructModalError(
          "Sorry, something went wrong. We've introduced extra security which may have caused your transaction to decline. Please try again.",
          () => { this.close.emit(false); }
        );
      }
    }
  };
  }

  getSelectedProductData() {
    const product = this.selectedProduct;
    if (!product) {
      this.constructModalError("Please choose a product to buy.");
      return null;
    }

    if (this.remainingAmount > 0 && this.useBalance) {
      product.Price.Amount = this.remainingAmount;
    }

    const productData = {
      productId: product?.ProductId,
      amountToPay: product?.Price?.Amount,
      offerId: product?.OfferId
    };
    return productData;
  }

  initialiseGooglePay(initialisationData: InitialisePaymentResult): void {
    if (!this.googlePayPaymentHelper) {
      this.errorService?.logError("InitialiseGooglePay failed because GooglePayPayment not defined", "initialiseGooglePay", "initialiseGooglePay", false);

      this.constructModalError(
        "Sorry, an error occured initialising the payment. Please try again.",
        () => { this.close.emit(false); }
      );
    }

    if (!this.googlePayPaymentHelper.enabled) {
      const googlePayButtonElement = document.querySelector("#googlepay-button");
      let commonCallbacksOptions = {
        paymentTypeDescription: "Google Pay"
      };
      let componentInstance: PaymentComponent = this;

      this.googlePayPaymentHelper.initialise(
        {
          config: {
            gatewayMerchantId: initialisationData?.GooglePayGatewayMerchantId,
            environment: initialisationData?.GooglePayEnvironment,
            merchantName: initialisationData?.GooglePayMerchantName,
            merchantId: initialisationData?.GooglePayMerchantId,
            countryCode: initialisationData?.IsoCountryCodeTwoLetter,
            currencyCode: initialisationData?.Currency
          },
          callbacks: Object.assign({}, this.createCommonCallbacks(commonCallbacksOptions), {
            onIsReadyToPayResult: function (isAccepted: any, buttonHtml: any) {
              if (isAccepted) {

                if (googlePayButtonElement) {
                  componentInstance.renderer?.removeClass(googlePayButtonElement, "hidden");
                  componentInstance.renderer?.appendChild(googlePayButtonElement, buttonHtml);
                  componentInstance.renderer?.listen(googlePayButtonElement, 'click', (event: any) => {
                    componentInstance.googlePayPaymentHelper.onGooglePaymentButtonClicked();
                  });

                }
              }
            }
          })
        }
      );
    }
  }

  initialisePayPal(initialisationData: InitialisePaymentResult): void {
    if (!this.payPalPaymentHelper) {
      this.errorService?.logError("InitialisePayPal failed because PayPalPaymentHelper not defined", "initialisePayPal", "initialisePayPal", false);

      this.constructModalError(
        "Sorry, an error occured initialising the payment. Please try again.",
        () => { this.close.emit(false); }
      );
    }

    if (!this.payPalPaymentHelper.enabled) {
      const payPalButtonElement = document.querySelector("#paypal-button");
      let commonCallbacksOptions = {
        paymentTypeDescription: "PayPal"
      };
      let componentInstance: PaymentComponent = this;

      this.payPalPaymentHelper.initialise(
        {
          config: {
            env: initialisationData?.PayPalEnvironment ?? ((this.config()?.IsTestSystem == false) ? "production" : "sandbox"),
          },
          callbacks: Object.assign({}, this.createCommonCallbacks(commonCallbacksOptions), {
            onIsReadyToPayResult: function (isAccepted: any) {
              if (isAccepted) {

                if (payPalButtonElement) {
                  componentInstance.renderer?.removeClass(payPalButtonElement, "hidden");
                }
              }
            }
          })
        }
      );
    }
  }

  isBundleProduct(product: ProductModel): boolean {
    return ProductHelper.isProductTypeBundle(product?.ProductTypeId);
  }

  isMinutesProduct(product: ProductModel): boolean {
    return ProductHelper.isProductTypeMinutesTopUp(product?.ProductTypeId);
  }

  isLoyaltyOffer(product: ProductModel): boolean {
    return ProductHelper.isOfferLoyalty(product?.OfferId);
  }

  isMultiProduct(): boolean {
    return this.productList?.length > 1;
  }

  isSelectedProduct(product: ProductModel): boolean {
    return product == this.selectedProduct;
  }

  selectedProductChanged(product: ProductModel): void {
    this.selectedProduct = product;

    if (this.isBundleProduct(product)) {
      this.calculateRemainingAmount(this.useBalance);
    }
  }

  manageCard() {
    if (this.manageCardsFunctionalityEnabled()) {
      // Check if we have product data before showing manage card
      let productData = this.getSelectedProductData();
      if (productData) {
        // We have data, show manage card
        this.showManageCard = true;
      }
    }
    else {
      let productData = this.selectedProduct;
      if (productData && productData.ProductId) window.location.href = this.moduleConfig?.UrlRegisterCardAndPay + productData.ProductId;
      else window.location.href = this.moduleConfig?.UrlRegisterCardAndPay;
    }
  }

  closeManageCard(eventData: boolean): void {
    this.showManageCard = false;
    if (eventData == true) {
      // Successful payment
      // Close and go to previous component
      this.close.emit(eventData);
    }
    else {
      // Unsuccessful payment
      // Reload data to allow for trying a different payment method
      this.getPaymentData();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onCloseIframe(event: Event): void {
    this.handleClickEvent(event);
    this.close.emit();
  }

  calculateRemainingAmount(checked: boolean): void {
    this.useBalance = checked;

    if (!this.useBalance || !this.selectedProduct || !this.member()?.AvailableBalance) {
      this.remainingAmount = this.selectedProduct?.Price?.Amount || 0;
      return;
    }
    
    let productPrice = this.selectedProduct?.Price?.Amount;
    let availableBalance = this.member()?.AvailableBalance?.Amount;
    this.remainingAmount = Math.max(0, productPrice - availableBalance);
    this.remainingAmountFormatted = this.resources?.CurrencySymbol + this.remainingAmount.toFixed(2);
  }
}
